import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {AdminService} from '../../../services/admin.service';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs/Subject';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import {Group} from '../../../models/group.model';
import {Position} from '../../../models/position.model';
import {DistributedStandard, StandardCleanupTarget, StandardDistributionRequest} from '../../../models/standard-distribution.model';
import {isStandardVersionConflict} from '../../../services/standard-version';
import {STANDARD_ASSIGNMENT_ERROR, standardAssignmentErrorCode, standardAssignmentErrorKey} from '../../../services/standard-assignment-error';

const GROUP_ROUTE_ID = 'id';
const FIRST_STANDARD = 0;
const STANDARD_RESPONSE_INDEX = 1;
const HTTP_NETWORK_ERROR = 0;
const HTTP_SERVER_ERROR = 500;
const HTTP_FORBIDDEN = 403;
const NO_DISTRIBUTION_ERROR = '';
const DISTRIBUTION_ERROR = {
  conflict: 'STANDARD_VERSION_CONFLICT_MESSAGE',
  failed: 'STANDARD_ASSIGNMENT_SAVE_FAILED',
  load: 'STANDARD_RELOAD_FAILED',
  group: 'ERROR_LOADING_DEPARTMENT',
  access: 'STANDARD_ASSIGNMENT_CLEANUP_ACCESS_LOST'
};

@Component({
  selector: 'app-standard-dissemination',
  templateUrl: './standard-dissemination.component.html',
  styleUrls: ['./standard-dissemination.component.css']
})
export class StandardDisseminationComponent implements OnInit, OnDestroy {
  standardsList: DistributedStandard[] = [];
  currentGroup: Group;
  selectedStandard: DistributedStandard = null;
  allGroupPositions: Position[] = [];
  selectedPositions: Position[] = [];
  originalSelectedPositions: Position[] = [];
  allEmployees = false;
  private originalAllEmployees = false;
  cleanupTargets: StandardCleanupTarget[] = [];
  isLoaded = false;
  pending = false;
  requiresReload = false;
  success = false;
  errorKey = NO_DISTRIBUTION_ERROR;
  private destroyed$ = new Subject<void>();

  constructor(private adminService: AdminService, private route: ActivatedRoute) {}

  ngOnInit(): void { this.reloadStandards(); }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get saveDisabled(): boolean {
    return this.pending || this.requiresReload || !this.selectedStandard || !this.hasChanges();
  }

  reloadStandards(): void {
    if (this.pending) { return; }
    this.pending = true;
    this.success = false;
    Observable.forkJoin(this.adminService.getGroupByID(this.route.snapshot.params[GROUP_ROUTE_ID]),
      this.adminService.getAllStandardsGroupWithoutSort()).takeUntil(this.destroyed$)
      .subscribe(responses => this.acceptLoaded(responses), () => this.handleLoadError(DISTRIBUTION_ERROR.load));
  }

  private acceptLoaded(responses: [HttpResponse<Group>, HttpResponse<Object>]): void {
    const group = responses[FIRST_STANDARD].body;
    if (!group) { this.handleLoadError(DISTRIBUTION_ERROR.group); return; }
    const standards = responses[STANDARD_RESPONSE_INDEX].body;
    if (!Array.isArray(standards)) { this.handleLoadError(DISTRIBUTION_ERROR.load); return; }
    this.acceptStandards(group, standards as DistributedStandard[]);
  }

  private acceptStandards(group: Group, standards: DistributedStandard[]): void {
    const previousId = this.selectedStandard && this.selectedStandard.id;
    this.currentGroup = group;
    this.standardsList = standards.filter(standard => standard.groupsWithPositions !== undefined);
    this.pending = false;
    this.isLoaded = true;
    this.requiresReload = false;
    this.errorKey = NO_DISTRIBUTION_ERROR;
    this.cleanupTargets = [];
    const selected = this.standardsList.find(standard => standard.id === previousId) || this.standardsList[FIRST_STANDARD];
    this.selectStandard(selected && selected.id);
  }

  private handleLoadError(errorKey: string): void {
    this.pending = false;
    this.isLoaded = true;
    this.requiresReload = true;
    this.errorKey = errorKey;
  }

  selectStandard(standardId: string): void {
    if (this.pending || this.requiresReload) { return; }
    this.success = false;
    const standard = this.standardsList.find(item => item.id === standardId);
    this.selectedStandard = standard ? Object.assign({}, standard) : null;
    const assignments = standard && (standard.groupsWithPositions || []).find(item => item.group.id === this.currentGroup.id);
    const assigned = assignments && assignments.positions || [];
    const positions = (this.currentGroup.positions || []).concat(
      (this.currentGroup.activeUsers || []).map(user => user.position)).filter(position => !!position);
    this.allGroupPositions = positions.concat(assigned).filter((position, index, list) =>
      list.findIndex(item => item.id === position.id) === index);
    this.selectedPositions = this.allGroupPositions.filter(position => assigned.some(item => item.id === position.id));
    this.originalSelectedPositions = this.selectedPositions.slice();
    this.allEmployees = this.originalAllEmployees = !!assignments && assignments.allEmployees === true;
  }

  selectPosItem(position: Position): void {
    if (this.pending || this.requiresReload) { return; }
    this.success = false;
    this.allEmployees = false;
    this.selectedPositions = !this.selectedPositions.includes(position)
      ? this.selectedPositions.concat(position) : this.selectedPositions.filter(item => item !== position);
  }

  toggleAllEmployees(selected: boolean): void {
    if (this.pending || this.requiresReload) { return; }
    this.success = false;
    this.allEmployees = selected;
    this.selectedPositions = [];
  }

  submitForm(): void {
    if (this.saveDisabled) { return; }
    const snapshot = Object.assign({}, this.selectedStandard);
    const request: StandardDistributionRequest = {
      standardId: snapshot.id, groupId: this.currentGroup.id,
      positions: this.selectedPositions.map(position => ({id: position.id})), allEmployees: this.allEmployees
    };
    this.pending = true;
    this.success = false;
    this.errorKey = NO_DISTRIBUTION_ERROR;
    this.adminService.disseminateStandard(request, snapshot).takeUntil(this.destroyed$).subscribe(
      response => this.acceptSavedStandard(response.body),
      error => this.handleSaveError(error, request)
    );
  }

  private acceptSavedStandard(standard: DistributedStandard): void {
    if (!standard || standard.id !== this.selectedStandard.id) { this.handleLoadError(DISTRIBUTION_ERROR.load); return; }
    this.standardsList = this.standardsList.map(item => item.id === standard.id ? standard : item);
    this.pending = false;
    this.selectStandard(standard.id);
    this.success = true;
  }

  private handleSaveError(error: HttpErrorResponse, request: StandardDistributionRequest): void {
    this.pending = false;
    this.requiresReload = true;
    this.errorKey = isStandardVersionConflict(error) ? DISTRIBUTION_ERROR.conflict
      : standardAssignmentErrorKey(error) || (error.status === HTTP_FORBIDDEN ? DISTRIBUTION_ERROR.access : DISTRIBUTION_ERROR.failed);
    const cleanup = standardAssignmentErrorCode(error) === STANDARD_ASSIGNMENT_ERROR.cleanup;
    const uncertain = error.status === HTTP_NETWORK_ERROR || error.status >= HTTP_SERVER_ERROR;
    if (cleanup || (!request.allEmployees && !request.positions.length && uncertain)) {
      this.cleanupTargets = [{id: request.standardId, label: this.selectedStandard.number || request.standardId}];
    }
  }

  trackItem(index: number, item: {id: string}): string { return item.id; }

  private hasChanges(): boolean {
    return this.allEmployees !== this.originalAllEmployees || this.selectedPositions.length !== this.originalSelectedPositions.length
      || this.selectedPositions.some(position => !this.originalSelectedPositions.some(item => item.id === position.id));
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/standard-dissemination/standard-dissemination.component.ts